// liveGet — how a read goes LIVE (ADR-074 §3): the hook's `queryFn` calls this
// instead of `apiClient.get`, and that one line is the whole opt-in. The answer's
// `Change-Tokens` header tells the tab's live client which change counters THIS
// exact question depends on, and the token each one had when the answer was
// made; from then on the client re-asks the question — while it is on screen —
// whenever a glance shows one of them moved. The page never sees a token, never
// sets a timer, and nobody hand-keeps a "what refreshes what" list.
//
// An answer that names no token (a read the server does not declare live) simply
// stays what it was: fetched on mount, refreshed by the person's own acts.
//
// An INFINITE question is as fresh as its FIRST page — the one asked with no
// page param (the cursor convention: `initialPageParam: undefined`). Later pages
// are read after it and counters only move forward, so the first page's tokens
// are the oldest the question holds; a look-again re-reads every loaded page.
//
// When the client re-asks by itself, the read is a BACKGROUND one: a 404 (the
// thing was deleted, the right was lost) must not throw the person off their
// page — the query fails quietly and its surface says so in place.
//
// The query's own abort signal rides the request: a read TanStack CANCELS (a
// "load older" pressed while every page was being re-read) never answers, so it
// never teaches the client a token for data that was thrown away — and leaving a
// page stops its read in flight.

import type { AxiosRequestConfig } from 'axios';
import type { QueryKey } from '@tanstack/react-query';
import { apiClient, projectIdOfUrl } from '../apiClient.js';
import { changeTokensOf } from './changeTokens.js';
import { liveClient } from './live.js';

/** The part of TanStack's query-function context a live read needs. */
interface LiveContext {
  queryKey: QueryKey;
  signal?: AbortSignal;
  pageParam?: unknown;
}

export async function liveGet<T>(ctx: LiveContext, url: string, config?: AxiosRequestConfig): Promise<T> {
  const background = liveClient.isLookingAgain(ctx.queryKey);
  // A look-again is the tab's own question: a 404 never navigates, a BUSY raises no toast.
  const answer = await apiClient.get<T>(url, { ...config, signal: ctx.signal, ...(background ? { quietNotFound: true, quietBusy: true } : {}) });
  const projectId = projectIdOfUrl(url);
  if (projectId && ctx.pageParam == null) {
    const tokens = changeTokensOf(answer.headers);
    liveClient.learn(projectId, ctx.queryKey, tokens ? { tokens } : null);
  }
  return answer.data;
}
